import { useCallback } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { apiFull } from "@/api/client";

interface PaginatedResult<T> {
  data: T[];
  total: number;
  counts: Record<string, number>;
}

/**
 * Rewritten on useQuery + `placeholderData: keepPreviousData` (D5, v5
 * replacement for `keepPreviousData: true`) — the previous page's data
 * stays on screen while the next page loads instead of flashing empty.
 *
 * `queryKey` is caller-supplied (derived from the query-key factory) so
 * each consumer's cache entries are keyed per-domain, not per-URL-string.
 */
export function useApiPaginated<T>(
  queryKey: readonly unknown[],
  url: string | null,
  page: number,
  pageSize: number,
  extraParams?: Record<string, string>,
  errorMessage = "Failed to load data",
  extraCountHeaders: string[] = [],
) {
  const query = useQuery<PaginatedResult<T>>({
    queryKey,
    queryFn: async () => {
      const offset = (page - 1) * pageSize;
      const params = new URLSearchParams({
        limit: String(pageSize),
        offset: String(offset),
      });
      for (const [key, value] of Object.entries(extraParams ?? {})) {
        if (value) params.set(key, value);
      }
      const { data, headers } = await apiFull<T[]>(`${url}?${params}`);
      const total = parseInt(headers.get("X-Total-Count") ?? "0", 10);
      const counts = Object.fromEntries(
        extraCountHeaders.map((h) => [h, parseInt(headers.get(h) ?? "0", 10)]),
      );
      return { data, total, counts };
    },
    enabled: url !== null,
    placeholderData: keepPreviousData,
  });

  const refetch = useCallback(() => {
    void query.refetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.refetch]);

  return {
    data: query.data?.data ?? [],
    total: query.data?.total ?? 0,
    counts: query.data?.counts ?? {},
    loading: query.isLoading,
    error: query.error ? errorMessage : "",
    refetch,
  };
}
